import { useId, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { utf8Encode, utf8Decode } from "@/lib/utf8";
import { parseByteList } from "./binaryBytes";
import { NoticeBox } from "@/components/NoticeBox";
import { copyText } from "@/lib/copyText";

// Everything goes through UTF-8 bytes, so "é" is 2 bytes (C3 A9) and emoji are 4,
// matching what files, networks and other tools actually contain.
type Source = "text" | "binary" | "hex" | "decimal";
const RADIX: Record<Exclude<Source, "text">, number> = { binary: 2, hex: 16, decimal: 10 };

const fromBytes = (bytes: Uint8Array, radix: number, pad: number) =>
  Array.from(bytes, b => b.toString(radix).padStart(pad, "0")).join(" ");

const textToBinary = (t: string) => fromBytes(utf8Encode(t), 2, 8);
const textToHex = (t: string) => fromBytes(utf8Encode(t), 16, 2);
const textToDecimal = (t: string) => fromBytes(utf8Encode(t), 10, 0);

const TOOL_INFO_CONTENT = {
  what: `Converts text between plain text, binary, hexadecimal, and decimal byte values (UTF-8, so accented letters, other scripts and emoji work), keeping all four representations in sync as you edit any one of them.`,
  how: `Type or paste into any of the four fields — text, binary, hex, or decimal — and the other three update automatically to match.`,
  tip: `Editing the binary or hex field directly is a genuinely useful way to understand how character encoding actually works, rather than just converting text one direction and moving on.`,
};

/**
 * Defined at module level, not inside the component -- it was previously
 * declared as a nested function inside BinaryAsciiConverter itself,
 * which meant a brand-new Field function got created on every render.
 * Since every keystroke triggers 4 setState calls here (text, binary,
 * hex, decimal all update together), React treated each new Field as a
 * completely different component type, unmounting and remounting the
 * underlying textarea DOM node after every single character -- which
 * silently dropped focus each time, so only the very first character of
 * whatever was typed actually landed. That's exactly what "not allowing
 * complete word to write" was describing. Hoisting this out fixes it,
 * since Field now stays the same stable reference across re-renders.
 */
function Field({
  label,
  value,
  onChange,
  onCopy,
  blocked,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onCopy: (v: string) => void;
  blocked: boolean;
  error?: string;
}) {
  const id = useId();
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label htmlFor={id} className="text-sm">{label}</Label>
        <button
          className="text-xs text-primary hover:underline disabled:opacity-50 disabled:pointer-events-none"
          onClick={() => onCopy(value)}
          disabled={blocked}
        >
          Copy
        </button>
      </div>
      <Textarea
        id={id}
        aria-invalid={error ? true : undefined}
        value={blocked ? "— blocked at today's usage limit —" : value}
        onChange={e => onChange(e.target.value)}
        readOnly={blocked}
        className="font-mono text-sm min-h-[80px]"
      />
      {error && <p role="alert" className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}

export default function BinaryAsciiConverter() {
  const [text, setText] = useState("Hello");
  const [binary, setBinary] = useState(textToBinary("Hello"));
  const [hex, setHex] = useState(textToHex("Hello"));
  const [decimal, setDecimal] = useState(textToDecimal("Hello"));

  const [error, setError] = useState<{ field: Source; message: string } | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const syncFrom = (source: Source, value: string) => {
    let bytes: Uint8Array;
    let warn: string | null = null;
    if (source === "text") {
      bytes = utf8Encode(value);
    } else {
      const parsed = parseByteList(value, RADIX[source]);
      if (!parsed.bytes) {
        // Keep what the user typed and leave the other fields alone.
        if (source === "binary") setBinary(value);
        if (source === "hex") setHex(value);
        if (source === "decimal") setDecimal(value);
        setError({ field: source, message: parsed.error ?? "Invalid input." });
        setWarning(null);
        return;
      }
      bytes = parsed.bytes;
      if (parsed.short) warn = "Some binary groups are shorter than 8 digits and were read as-is (padded with leading zeros).";
      try {
        utf8Decode(bytes, true);
      } catch {
        warn = "These bytes are not valid UTF-8, so the Text field shows replacement characters (�). The binary, hex and decimal fields keep your exact bytes.";
      }
    }
    setError(null);
    setWarning(warn);
    setText(source === "text" ? value : utf8Decode(bytes, false));
    setBinary(source === "binary" ? value : fromBytes(bytes, 2, 8));
    setHex(source === "hex" ? value : fromBytes(bytes, 16, 2));
    setDecimal(source === "decimal" ? value : fromBytes(bytes, 10, 0));
  };

  const copy = (value: string) => {
    void copyText(value, "Copied to clipboard");
  };

  // Was called once per Field instance (4 separate, independent
  // useLogToolUsage calls for one tool) -- moved to the actual
  // component level, called once, with blocked passed down to each
  // Field to hide its value instead.
  const { blocked } = useLogToolUsage(true, 3000, { ownActions: true });

  return (
    <ToolContainer>
      <div className="space-y-4">
        <Field label="Text" value={text} onChange={v => syncFrom("text", v)} onCopy={copy} blocked={blocked} />
        <Field
          label="Binary"
          value={binary}
          onChange={v => syncFrom("binary", v)}
          onCopy={copy}
          blocked={blocked}
          error={error?.field === "binary" ? error.message : undefined}
        />
        <Field
          label="Hexadecimal"
          value={hex}
          onChange={v => syncFrom("hex", v)}
          onCopy={copy}
          blocked={blocked}
          error={error?.field === "hex" ? error.message : undefined}
        />
        <Field
          label="Decimal (byte values)"
          value={decimal}
          onChange={v => syncFrom("decimal", v)}
          onCopy={copy}
          blocked={blocked}
          error={error?.field === "decimal" ? error.message : undefined}
        />
      </div>
      {warning && <NoticeBox className="text-xs mt-3" role="status">{warning}</NoticeBox>}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
